import { useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import {
  mcpConnectionDisconnectService,
  mcpConnectionsGetService,
} from 'src/api/mcp-connection'

// The page updates on its own once someone signs in from their AI tool.
const POLL_INTERVAL = 10000

/**
 * Connection status of each AI tool in the workspace, the MCP server URL and
 * `disconnect(tool)` for the current user's own connections.
 */
const useMcpConnections = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [state, setState] = useState({ mcpUrl: '', tools: [], loaded: false })

  const load = useCallback(
    () =>
      mcpConnectionsGetService()
        .then(response => {
          const data = response?.data || {}
          setState({
            mcpUrl: data.mcpUrl || '',
            tools: data.tools || [],
            loaded: true,
          })
        })
        .catch(() => setState(current => ({ ...current, loaded: true }))),
    [],
  )

  useEffect(() => {
    load()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, POLL_INTERVAL)
    return () => clearInterval(timer)
  }, [load])

  const disconnect = useCallback(
    tool =>
      mcpConnectionDisconnectService(tool)
        .then(() => {
          enqueueSnackbar('Disconnected', { variant: 'success' })
          return load()
        })
        .catch(error =>
          enqueueSnackbar(
            error?.response?.data?.operationMessage || 'Could not disconnect.',
            { variant: 'error' },
          ),
        ),
    [enqueueSnackbar, load],
  )

  const byTool = Object.fromEntries(state.tools.map(item => [item.tool, item]))

  return { ...state, byTool, disconnect }
}

export default useMcpConnections
